import { FC, useState } from "react"
import { Trash2, ChevronRight, ChevronDown, List, Info } from "lucide-react"
import {
  ConnectorDataType,
  ConnectorFieldMappingEntry
} from "../../../../../../../types"
import { getFieldDefinition } from "../../../../../../constants/connector-field-types"
import ValueTransformEditor from "./ValueTransformEditor"
import ConnectorFormInput from "../../components/ConnectorFormInput"

interface FieldMappingRowProps {
  stableKey: string
  index: number
  entry: ConnectorFieldMappingEntry
  isIncoming: boolean
  dataType: ConnectorDataType
  bbField: string
  crmField: string
  isExpanded: boolean
  onUpdateMapping: (
    index: number,
    newBbField: string,
    newCrmField: string
  ) => void
  onRemove: (index: number) => void
  onToggleExpanded: () => void
  onUpdateValueTransform: (
    index: number,
    sourceValue: string,
    targetValue: string,
    replaceKey?: string
  ) => void
  onRemoveValueTransform: (index: number, sourceValue: string) => void
  onRemoveAllValueTransforms: (index: number) => void
  onUpdateDefaultIfEmpty: (index: number, value: string) => void
  onCollapseField: () => void
  valueTransforms: Record<string, any>
}

const FieldMappingRow: FC<FieldMappingRowProps> = ({
  stableKey,
  index,
  entry,
  isIncoming,
  dataType,
  bbField,
  crmField,
  isExpanded,
  onUpdateMapping,
  onRemove,
  onToggleExpanded,
  onUpdateValueTransform,
  onRemoveValueTransform,
  onRemoveAllValueTransforms,
  onUpdateDefaultIfEmpty,
  onCollapseField,
  valueTransforms
}) => {
  const [showValueMapping, setShowValueMapping] = useState(false)
  const fieldDef = getFieldDefinition(bbField, dataType)
  const isSelectField = fieldDef?.type === "select"
  const hasTransforms = Object.keys(valueTransforms).length > 0
  const hasDefaultIfEmpty = entry.default_if_empty !== undefined
  const hasExpandedContent = hasTransforms || hasDefaultIfEmpty

  // Generate human-readable label for custom fields
  const getFieldLabel = (fieldPath: string): string => {
    if (fieldDef?.label) return fieldDef.label

    // For custom fields, convert path to readable label
    const parts = fieldPath.split(".")
    const fieldName = parts[parts.length - 1]

    return fieldName
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const displayLabel = getFieldLabel(bbField)

  return (
    <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
      <div className="grid grid-cols-6 gap-2">
        {/* Left column */}
        <div className="col-span-2">
          {isIncoming ? (
            <ConnectorFormInput
              label=""
              value={crmField}
              onChange={value => onUpdateMapping(index, bbField, value)}
              placeholder="e.g., homebuyer.email"
            />
          ) : (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 text-base flex items-center justify-between">
              <div className="flex items-center">
                {displayLabel}
                {isSelectField && (
                  <span className="text-xs text-purple-600 ml-1">(select)</span>
                )}
              </div>
              <div className="group relative">
                <Info
                  size={16}
                  className="text-gray-400 hover:text-gray-600 cursor-help"
                />
                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-10">
                  <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                    {bbField}
                    <div className="absolute top-full right-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="col-span-3">
          {isIncoming ? (
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 text-base flex items-center justify-between">
              <div className="flex items-center">
                {displayLabel}
                {isSelectField && (
                  <span className="text-xs text-purple-600 ml-1">(select)</span>
                )}
              </div>
              <div className="group relative">
                <Info
                  size={16}
                  className="text-gray-400 hover:text-gray-600 cursor-help"
                />
                <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-10">
                  <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                    {bbField}
                    <div className="absolute top-full right-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ConnectorFormInput
              label=""
              value={crmField}
              onChange={value => onUpdateMapping(index, bbField, value)}
              placeholder="e.g., FirstName or name.first"
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-1">
          <button
            type="button"
            onClick={onToggleExpanded}
            className={`p-1 rounded ${
              hasExpandedContent
                ? "text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                : "text-blue-600 hover:text-blue-800 hover:bg-blue-50"
            }`}
            title={
              hasExpandedContent
                ? isExpanded
                  ? "Hide value mapping"
                  : "Show value mapping"
                : "Map values"
            }
          >
            {hasExpandedContent && isExpanded ? (
              <ChevronDown size={16} />
            ) : hasExpandedContent ? (
              <ChevronRight size={16} />
            ) : (
              <List size={16} />
            )}
          </button>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
            title="Remove field"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Expanded options: default if empty + value transforms */}
      {isExpanded && (
        <div className="mt-3 space-y-3">
          {hasTransforms ? (
            <ValueTransformEditor
              key={stableKey}
              index={index}
              isIncoming={isIncoming}
              isSelectField={isSelectField}
              fieldDef={fieldDef}
              valueTransforms={valueTransforms}
              hasTransforms={hasTransforms}
              onUpdateValueTransform={onUpdateValueTransform}
              onRemoveValueTransform={onRemoveValueTransform}
              onRemoveAllValueTransforms={onRemoveAllValueTransforms}
              onCollapseField={onCollapseField}
            />
          ) : showValueMapping ? (
            <ValueTransformEditor
              key={stableKey}
              index={index}
              isIncoming={isIncoming}
              isSelectField={isSelectField}
              fieldDef={fieldDef}
              valueTransforms={valueTransforms}
              hasTransforms={hasTransforms}
              onUpdateValueTransform={onUpdateValueTransform}
              onRemoveValueTransform={onRemoveValueTransform}
              onRemoveAllValueTransforms={onRemoveAllValueTransforms}
              onCollapseField={onCollapseField}
            />
          ) : (
            <>
              <div className="flex items-start gap-2">
                <div className="w-48">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Default if empty
                  </label>
                  <input
                    type="text"
                    value={
                      entry.default_if_empty !== undefined
                        ? String(entry.default_if_empty)
                        : ""
                    }
                    onChange={e =>
                      onUpdateDefaultIfEmpty(index, e.target.value)
                    }
                    placeholder="Leave empty to skip"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="pt-5">
                  <span className="text-xs text-gray-400">
                    Default if empty or missing
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowValueMapping(true)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                + Add value mapping
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default FieldMappingRow
